import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore, Security, Save, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DetailHeader from '../components/DetailHeader';
import TenantResourceCopyDialog from '../components/TenantResourceCopyDialog';
import api from '../services/api';
import type { Permission, PermissionGroup } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';

interface FormData {
  name: string;
  description: string;
  scope: 'system' | 'tenant';
  permissionCodenames: string[];
}

const initialFormData: FormData = {
  name: '',
  description: '',
  scope: 'tenant',
  permissionCodenames: [],
};

type GroupPermissions = Record<string, Permission[]>;

export default function GroupFormPage() {
  const { t } = useTranslation(['pages', 'common', 'permissions']);
  const navigate = useNavigate();
  const { user, currentTenant, isSuperuser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const descriptor = formData.name ? formData.name : undefined;
  usePageTitle(t('pages:groups.title'), descriptor);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [existingGroup, setExistingGroup] = useState<PermissionGroup | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySubmitting, setCopySubmitting] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const canCopyResource = Boolean(
    isEditing
      && existingGroup?.id
      && existingGroup.scope === 'tenant'
      && !existingGroup.isSystem
      && isSuperuser
      && user?.tenantId
      && currentTenant?.id
      && currentTenant.id === user.tenantId,
  );

  const fetchGroupRef = useRef<(groupId: string) => Promise<void>>(async () => undefined);

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      void fetchGroupRef.current(id);
    } else {
      setLoading(false);
    }
  }, [id, isEditing]);

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/api/permissions/available/');
      setPermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error loading permissions:', err);
    }
  };

  const fetchGroup = async (groupId: string) => {
    try {
      const response = await api.get(`/api/permission-groups/${groupId}/`);
      const group: PermissionGroup = response.data;
      setExistingGroup(group);
      const filteredPermissionCodenames = (group.permissions || [])
        .filter((codename) => !codename.startsWith('costs_'));
      setFormData({
        name: group.name,
        description: group.description || '',
        scope: group.scope,
        permissionCodenames: filteredPermissionCodenames,
      });
      setGroupId(group.id ?? null);
    } catch (err) {
      console.error('Error loading group:', err);
      setError(t('pages:groups.errors.loading') || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  fetchGroupRef.current = fetchGroup;

  const groupedPermissions = useMemo<GroupPermissions>(() => {
    const filtered = permissions.filter((perm) => !perm.codename.startsWith('costs_'));
    return filtered.reduce((acc, perm) => {
      const codename = perm.codename;
      let category = codename === 'view_metrics' ? 'metrics' : codename.split('_')[0];
      if (['add', 'change', 'delete', 'view'].includes(category) && codename.includes('_')) {
        category = codename.split('_').slice(1).join('_');
      } else if (codename.startsWith('tenant_') || codename.startsWith('tenants_')) {
        category = 'tenants';
      } else if (codename.startsWith('contact_z_')) {
        category = 'contact_z';
      } else if (codename.startsWith('integrations_')) {
        category = 'integrations';
      }
      const categoryMap: Record<string, string> = {
        group: 'groups',
        tenantgroup: 'tenantgroups',
        permission: 'permissions',
        tenant: 'tenants',
        user: 'users',
      };
      category = categoryMap[category] || category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(perm);
      return acc;
    }, {} as GroupPermissions);
  }, [permissions]);

  const handleTogglePermission = (permId: number) => {
    const permCodename = permissions.find((perm) => perm.id === permId)?.codename;
    if (!permCodename) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      permissionCodenames: prev.permissionCodenames.includes(permCodename)
        ? prev.permissionCodenames.filter((codename) => codename !== permCodename)
        : [...new Set([...prev.permissionCodenames, permCodename])],
    }));
  };

  const handleToggleCategory = (category: string) => {
    const categoryPermCodenames = groupedPermissions[category].map((perm) => perm.codename);
    const allSelected = categoryPermCodenames.every((codename) =>
      formData.permissionCodenames.includes(codename),
    );
    setFormData((prev) => ({
      ...prev,
      permissionCodenames: allSelected
        ? prev.permissionCodenames.filter((codename) => !categoryPermCodenames.includes(codename))
        : [...new Set([...prev.permissionCodenames, ...categoryPermCodenames])],
    }));
  };

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        scope: formData.scope,
        permissionCodenames: formData.permissionCodenames,
      };
      if (isEditing && id) {
        await api.patch(`/api/permission-groups/${id}/`, payload);
      } else {
        await api.post('/api/permission-groups/', payload);
      }
      navigate('/groups');
    } catch (err: unknown) {
      console.error('Error saving group:', err);
      setError(t('pages:groups.errors.saving') || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  const headerTitle = isEditing
    ? formData.name || t('pages:groups.editTitle')
    : t('pages:groups.newTitle');

  const breadcrumbs = [
    { label: t('common:menu.groups'), to: '/groups' },
    { label: isEditing ? t('pages:groups.edit') : t('pages:groups.new') },
  ];

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {canCopyResource && (
        <Button
          variant="outlined"
          startIcon={<ContentCopy />}
          onClick={() => {
            setCopyError(null);
            setCopyDialogOpen(true);
          }}
          disabled={saving || copySubmitting}
        >
          {t('pages:groups.copy.button')}
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
        onClick={() => handleSubmit()}
        disabled={saving || !formData.name}
      >
        {saving ? t('common:actions.saving') : t('common:actions.save')}
      </Button>
    </Box>
  );

  const handleCopyToTenant = async (targetTenantId: string) => {
    if (!id) {
      return;
    }
    setCopySubmitting(true);
    setCopyError(null);
    try {
      const response = await api.post(`/api/permission-groups/${id}/copy_to_tenant/`, {
        targetTenantId,
      });
      setCopyDialogOpen(false);
      setSuccess(
        response.data.groupReused
        ? t('pages:groups.copy.successReused', {
          targetTenantName: response.data.targetTenantName,
          name: response.data.group?.name || formData.name,
        })
        : t('pages:groups.copy.successCreated', {
          targetTenantName: response.data.targetTenantName,
          name: response.data.group?.name || formData.name,
        }),
      );
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { detail?: string; error?: string } } };
      setCopyError(
        apiError?.response?.data?.detail
          || apiError?.response?.data?.error
          || t('pages:groups.copy.error'),
      );
    } finally {
      setCopySubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        title={headerTitle}
        sectionIcon={<Security sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={groupId}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/groups"
      />

      <TenantResourceCopyDialog
        open={copyDialogOpen}
        title={t('pages:groups.copy.title')}
        description={t('pages:groups.copy.description')}
        submitting={copySubmitting}
        error={copyError}
        sourceTenantId={user?.tenantId}
        onClose={() => {
          if (copySubmitting) {
            return;
          }
          setCopyDialogOpen(false);
          setCopyError(null);
        }}
        onSubmit={handleCopyToTenant}
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label={t('pages:groups.name')}
          fullWidth
          value={formData.name}
          onChange={(event) => setFormData({ ...formData, name: event.target.value })}
          required
        />
        <TextField
          label={t('pages:groups.description')}
          fullWidth
          multiline
          rows={2}
          value={formData.description}
          onChange={(event) => setFormData({ ...formData, description: event.target.value })}
        />
        <FormControl fullWidth disabled={!isSuperuser}>
          <InputLabel>{t('pages:groups.scope')}</InputLabel>
          <Select
            value={formData.scope}
            label={t('pages:groups.scope')}
            onChange={(event) =>
              setFormData({ ...formData, scope: event.target.value as 'system' | 'tenant' })
            }
          >
            {isSuperuser && (
              <MenuItem value="system">{t('pages:groups.scopeSystem')}</MenuItem>
            )}
            <MenuItem value="tenant">{t('pages:groups.scopeTenant')}</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="subtitle1" fontWeight={600}>
          {t('pages:groups.permissions')}
        </Typography>

        <Divider />

        {Object.entries(groupedPermissions).map(([category, perms]) => {
          const categoryPermCodenames = perms.map((perm) => perm.codename);
          const selectedCount = categoryPermCodenames.filter((codename) =>
            formData.permissionCodenames.includes(codename),
          ).length;
          const allSelected = selectedCount === perms.length;

          const description = t(`permissions:categoryDescriptions.${category}`, { defaultValue: '' });
          return (
            <Accordion key={category} defaultExpanded={selectedCount > 0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedCount > 0 && !allSelected}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleToggleCategory(category);
                    }}
                  />
                  <Box>
                    <Typography sx={{ textTransform: 'capitalize' }}>
                      {t(`permissions:categories.${category}`, { defaultValue: category })}
                    </Typography>
                    {description && (
                      <Typography variant="caption" color="text.secondary">
                        {description}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${selectedCount}/${perms.length}`}
                    size="small"
                    color={selectedCount > 0 ? 'primary' : 'default'}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {perms.map((perm) => (
                    <FormControlLabel
                      key={perm.id}
                      control={
                        <Checkbox
                          checked={formData.permissionCodenames.includes(perm.codename)}
                          onChange={() => handleTogglePermission(perm.id)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t(`permissions:permissions.${perm.codename}`, {
                            defaultValue: perm.name,
                          })}
                        </Typography>
                      }
                      sx={{
                        minWidth: 200,
                        m: 0,
                        bgcolor: formData.permissionCodenames.includes(perm.codename)
                          ? 'action.selected'
                          : 'transparent',
                        borderRadius: 1,
                        px: 1,
                      }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}
